import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Plus, Minus, Wallet } from 'lucide-react';
import { Button, TableHeader, CardStats, SearchInput, Pagination, ExportDropdown } from '@components/ui';
import { TablePageSkeleton } from '@components/skeleton';
import { usePagination, useExportExcel } from '@hooks';
import { getPaymentsByConstructionId, getPaymentStats } from '@mocks/odemeler';
import OdemelerTablosu from './OdemelerTablosu';
import GelirModal from './GelirModal';
import GiderModal from './GiderModal';



export default function Payments() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);
    const constructionId = showAllConstructions ? null : selectedConstruction?.id;

    const [incomeModalOpen, setIncomeModalOpen] = useState(false);
    const [expenseModalOpen, setExpenseModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // Export hook
    const { exportToExcel, isExporting } = useExportExcel();

    const allPayments = getPaymentsByConstructionId(constructionId);
    const stats = getPaymentStats(constructionId);

    // Filter payments based on search query
    const payments = allPayments.filter(payment => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            payment.description?.toLowerCase().includes(query) ||
            payment.person?.toLowerCase().includes(query) ||
            payment.category?.toLowerCase().includes(query)
        );
    });

    // Pagination
    const {
        paginatedData,
        currentPage,
        totalPages,
        totalItems,
        pageSize,
        goToPage,
        resetPage,
    } = usePagination(payments, 10);

    // Reset pagination when search changes
    useEffect(() => {
        resetPage();
    }, [searchQuery]);


    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0,
        }).format(amount);
    };

    const handleAddIncome = (data) => {
        console.log('Gelir eklendi:', data);
        toast.success(`${formatCurrency(data.amount)} tutarında gelir kaydedildi`);
    };

    const handleAddExpense = (data) => {
        console.log('Gider eklendi:', data);
        toast.success(`${formatCurrency(data.amount)} tutarında gider kaydedildi`);
    };

    // Loading state - API entegrasyonunda true olarak başlatılacak
    const [isLoading] = useState(false);
    if (isLoading) return <TablePageSkeleton statsCount={4} tableRows={10} tableColumns={5} />;

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <CardStats
                    title="Toplam Gelir"
                    value={formatCurrency(stats.totalIncome)}
                    icon={Plus}
                    color="success"
                    size="sm"
                />
                <CardStats
                    title="Toplam Gider"
                    value={formatCurrency(stats.totalExpense)}
                    icon={Minus}
                    color="error"
                    size="sm"
                />
                <CardStats
                    title="Net Bakiye"
                    value={formatCurrency(stats.totalIncome - stats.totalExpense)}
                    icon={Wallet}
                    color="primary"
                    size="sm"
                />
                <CardStats
                    title="İşlem Sayısı"
                    value={stats.incomeCount + stats.expenseCount}
                    icon={Wallet}
                    color="tertiary"
                    size="sm"
                />
            </div>

            {/* Table Header */}
            <TableHeader
                title="Ödemeler"
                subtitle={showAllConstructions ? 'Tüm inşaatlar' : selectedConstruction?.name}
            >
                <SearchInput
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Açıklama, kişi veya kategori ara..."
                    size="md"
                />
                <ExportDropdown
                    onExportExcel={() => exportToExcel({
                        data: payments,
                        columns: [
                            { header: 'Tarih', key: 'date', format: 'date' },
                            { header: 'Açıklama', key: 'description' },
                            { header: 'Kişi', key: 'person' },
                            { header: 'Kategori', key: 'category' },
                            { header: 'Tür', key: 'type' },
                            { header: 'Tutar', key: 'amount', format: 'currency' },
                        ],
                        fileName: 'odemeler_raporu',
                        sheetName: 'Ödemeler',
                    })}
                    isExporting={isExporting}
                    disabled={payments.length === 0}
                />
                <Button

                    variant="success"
                    icon={<Plus size={18} />}
                    onClick={() => setIncomeModalOpen(true)}
                >
                    Gelir Ekle
                </Button>
                <Button
                    variant="error"
                    icon={<Minus size={18} />}
                    onClick={() => setExpenseModalOpen(true)}
                >
                    Gider Ekle
                </Button>
            </TableHeader>

            {/* Table */}
            <OdemelerTablosu data={paginatedData} />

            {/* Pagination */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={goToPage}
            />


            {/* Modals */}
            <GelirModal
                isOpen={incomeModalOpen}
                onClose={() => setIncomeModalOpen(false)}
                onSubmit={handleAddIncome}
            />
            <GiderModal
                isOpen={expenseModalOpen}
                onClose={() => setExpenseModalOpen(false)}
                onSubmit={handleAddExpense}
            />
        </div>
    );
}
